import { stripLocale, localeFromPath, localizedRoutes } from './i18n/paths'
import type { Locale } from './i18n/locale'
import { LocaleProvider } from './i18n/LocaleProvider'
import { lazy, Suspense } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import ScrollToTop from './components/ScrollToTop'
import HomePage from './pages/HomePage'
import BlogPage from './pages/BlogPage'
import BlogPostPage from './pages/BlogPostPage'
import NewsroomPage from './pages/NewsroomPage'
import CompanyPage from './pages/CompanyPage'
import AnnouncementsPage from './pages/AnnouncementsPage'
import StatusPage from './pages/StatusPage'
import AboutPage from './pages/AboutPage'
import ConfiguratorPage from './pages/ConfiguratorPage'
import TermsPage from './pages/TermsPage'
import PrivacyPage from './pages/PrivacyPage'
import NotFoundPage from './pages/NotFoundPage'
import { InquiryProvider } from './components/InquirySheet'
import './i18n/localization.css'

const BlogStudioPage = lazy(() => import('./pages/BlogStudioPage'))
const ButtonLabPage = import.meta.env.DEV
  ? lazy(() => import('./pages/ButtonLabPage'))
  : null

export default function App({ locale }: { locale?: Locale }) {
  const location = useLocation()
  const base = stripLocale(location.pathname)
  const isJapaneseRoute = localeFromPath(location.pathname) === 'ja'
  const supported = localizedRoutes.includes(base.replace(/\/+$/, '') || '/')
  const pathname = isJapaneseRoute && !supported ? '/404' : base
  return (
    <LocaleProvider locale={locale}>
      <InquiryProvider>
        <ScrollToTop />
        <Routes location={{ ...location, pathname }}>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/configurator" element={<ConfiguratorPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage key="blog" />} />
          <Route path="/newsroom" element={<NewsroomPage />} />
          <Route path="/newsroom/:slug" element={<BlogPostPage key="newsroom" publication="newsroom" />} />
          <Route path="/company" element={<CompanyPage />} />
          <Route path="/announcements" element={<AnnouncementsPage />} />
          <Route path="/status" element={<StatusPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {ButtonLabPage && (
            <Route
              path="/button-lab"
              element={(
                <Suspense fallback={null}>
                  <ButtonLabPage />
                </Suspense>
              )}
            />
          )}
          <Route
            path="/backstage/bybeit-blog"
            element={(
              <Suspense fallback={<main className="studio-gate"><p>Opening studio…</p></main>}>
                <BlogStudioPage />
              </Suspense>
            )}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </InquiryProvider>
    </LocaleProvider>
  )
}
